/**
 * Flows to account for:
 * - User is on a chat page, chat gets loaded in SSR, needs to populate the store on client
 * - User submits prompt on homepage, needs to navigate to chat page once there's a chatId while keeping the streaming alive
 * - User navigates to a chat page, needs to fetch the chat from the server and populate the store on the client
 * - User navigates away from a chat page, needs to stop the streaming and clear the store
 * - User scrolls up in a chat, there's more messages to load, needs to fetch them and add to the store. UI needs to load them seamlessly while preserving the scroll position.
 */

import { type Params, useNavigate } from "@solidjs/router";
import { type LanguageRegions, newStreamRequest, type RichText, type Scope } from "@storytell/cp-client";
import {
  StreamingChatStatus,
  type StreamingMessageId,
  type StreamingOperationId,
  type StreamingChatId,
  type StreamingService,
  type StreamingOperation,
  type StreamingMessage,
  type StreamingBlock,
  type StreamingBlockId,
} from "@storytell/streaming";
import { createStore, type SetStoreFunction } from "solid-js/store";
import { createMemo, createRenderEffect, untrack } from "solid-js";
import { urls } from "~/lib/urls";
import { dispatchGlobalEvent } from "@storytell/events";
import { createChatServiceResource } from "./chatServiceResource";
import { createChatServiceScope } from "./chatServiceScope";

export type ChatService = ReturnType<typeof createChatService>;

export type ChatServiceState = {
  chatId: StreamingChatId | undefined;
  error: Error | undefined;
  isStreaming: boolean;
};
export type SetChatServiceState = SetStoreFunction<ChatServiceState>;

/**
 * High-level manager for handling chat functionality.
 * Resposible for:
 * - Navigation between chats
 * - Chat creation
 * - Chat playback
 * - UI state management
 *
 * The logic for storing and streaming chats is handled by the streaming package.
 * This service wraps extra logic around it.
 */
export const createChatService = (deps: {
  urlParams: () => Params;
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => string | undefined;
  stream: StreamingService;
}) => {
  const navigate = useNavigate();

  const [state, setState] = createStore<ChatServiceState>({
    chatId: deps.urlParams().chatId as StreamingChatId | undefined,
    error: undefined,
    isStreaming: false,
  });

  const resource = createChatServiceResource({
    authReady: deps.authReady,
    authToken: deps.authToken,
    urlParams: deps.urlParams,
    state,
    setState,
    stream: deps.stream,
  });

  /**
   * Ensures that whenever a new chat is loaded, we process it to populate the store
   * It's the store that the UI pulls data from, not the resource directly
   */
  createRenderEffect(() => {
    if (resource.latest?.data) {
      untrack(() => {
        if (resource.latest?.data) {
          deps.stream.chat.processChatRecording(resource.latest.data);
        }
      });
    }
  });

  /**
   * Main handler for submitting a prompt
   */
  const startOrContinueChat = (opts: {
    submission: RichText.RichText;
    locale: LanguageRegions.LanguageRegion;
    projectId: string;
    workspaceId: string;
    scope: Scope.Scope;
  }) => {
    const chat = getChat();
    const chatId = chat?.id;

    const parentMessageId = chat?.messages[0];

    setState("isStreaming", true);

    const request = newStreamRequest(
      chatId || "",
      opts.submission,
      opts.locale,
      false,
      opts.projectId,
      opts.workspaceId,
      opts.scope,
      parentMessageId,
    );
    const token = deps.authToken();

    let capturedOnce = false;
    const onChatIdCaptured = (chatId: StreamingChatId) => {
      if (capturedOnce) return;
      dispatchGlobalEvent("new-chat-created", {
        id: chatId,
        title: "New Chat",
      });
      dispatchGlobalEvent("prompt-submitted-successfuly", undefined);
      capturedOnce = true;
      setState({
        chatId,
        error: undefined,
      });
      navigate(urls.chatPage(chatId));
    };

    deps.stream.chat.startStreamingChat({
      request: request,
      options: {
        bearerToken: token,
      },
      onChatIdCaptured,
      onMessageFinished: () => {
        setState("isStreaming", false);
      },
    });
  };

  const getChatId = () => state.chatId;
  const getChat = createMemo(() => (state.chatId ? deps.stream.state.chats[state.chatId] : undefined));
  const getChatError = () => state.error || resource()?.error;
  const isChatStreaming = () =>
    state.isStreaming || (state.chatId && deps.stream.chat.getStatus(state.chatId) === StreamingChatStatus.GENERATING);
  const isChatIdle = () => !isChatStreaming();
  const isChatLoading = () => resource.loading;

  const shouldShowContent = () => {
    if (getChatId()) return true;
    if (isChatStreaming()) return true;
    if (isChatLoading()) return true;

    return false;
  };

  const getOperation = (operationId: StreamingOperationId) => deps.stream.operation.get(operationId);
  const getMessageOperations = (messageId: StreamingMessageId) =>
    deps.stream.message
      .get(messageId)
      ?.operations.map((op) => deps.stream.operation.get(op))
      .filter((op): op is StreamingOperation => op !== undefined);
  const getMessage = (messageId: StreamingMessageId) => deps.stream.message.get(messageId);
  const getChatMessages = () =>
    getChatId()
      ? deps.stream.chat
          .get(getChatId()!)
          ?.messages.map((m) => deps.stream.message.get(m))
          .filter((m): m is StreamingMessage => m !== undefined)
      : undefined;
  const getBlock = (blockId: StreamingBlockId) => deps.stream.block.get(blockId);
  const getOperationBlocks = (operationId: StreamingOperationId) =>
    deps.stream.operation
      .get(operationId)
      ?.blocks.map((b) => deps.stream.block.get(b))
      .filter((b): b is StreamingBlock => b !== undefined);
  const getOperationTask = (operationId: StreamingOperationId) => deps.stream.operation.get(operationId)?.task;

  return {
    scope: createChatServiceScope({
      projectId: () => deps.projectId(),
    }),
    getChatId,
    shouldShowContent,
    getChat,
    getChatError,
    isChatStreaming,
    isChatIdle,
    isChatLoading,
    startOrContinueChat,
    getOperation,
    getMessageOperations,
    getMessage,
    getChatMessages,
    getBlock,
    getOperationBlocks,
    getOperationTask,
  };
};
